import { useContext, useState } from 'react';
import { compose, flatten, map, path, pipe } from 'ramda';
import { branch, withProps } from 'recompose';
import { useMountEffect } from 'js/hooks/useMountEffect';
import withLeaderBoardsReducer from '../common/withLeaderBoardsReducer';
import withSummaryUpdates from '../common/withSummaryUpdates';
import { withFetchListActions } from '../withLeaderBoardList';
import { fetchActiveList, fetchHistoricalList, setListAction } from '../actions';
import { ListContext } from '../context';
import { withListContext } from '../withContext';
import { statuses } from '../constants';
import { NOTIFICATION_FIELDS } from './constants';

export const useFetchingLeaderBoards = (props) => {
    const { state: { leaderboards }, dispatch } = useContext(ListContext);
    const [error, setError] = useState();
    const {
        presentUpcomingRaces,
        presentOnAirRaces,
        presentEndedRaces,
        presentPendingRaces,
        maxEndedRacesQuantity,
        racesOrder,
        ...rest
    } = props;

    useMountEffect((isMounted) => {
        let promises = [];

        if (presentOnAirRaces || presentUpcomingRaces || presentPendingRaces) {
            promises = [
                ...promises, fetchActiveList({
                    ...rest,
                    filterByStatus: [
                        presentOnAirRaces && statuses.ACTIVE,
                        presentUpcomingRaces && statuses.NOT_STARTED,
                        presentPendingRaces && statuses.PENDING_REVIEW
                    ].filter(Boolean),
                    sortOptions: [{ fieldName: 'leaderboardEndTime', sortDirection: 'desc' }]
                })
            ];
        }

        if (presentEndedRaces) {
            promises = [
                ...promises,
                fetchHistoricalList({
                    ...rest,
                    startFrom: 0,
                    count: maxEndedRacesQuantity,
                    sortOptions: [{ fieldName: 'leaderboardEndTime', sortDirection: 'desc' }]
                })
            ];
        }

        const mergeResponses = pipe(
            map(path(['data', 'leaderboards'])),
            flatten
        );

        Promise.all(promises)
            .then((responses) => {
                if (isMounted()) {
                    const races = mergeResponses(responses);
                    dispatch(setListAction(races));
                }
            })
            .catch(({ data }) => isMounted() && setError(data));
    }, []);

    return { leaderboards, error };
};

export const withFetch = compose(
    withFetchListActions,
    withProps(useFetchingLeaderBoards)
);

export const withNotificationFieldsWhitelist = withProps(
    () => ({
        notificationFieldsWhitelist: NOTIFICATION_FIELDS
    })
);

export default compose(
    withLeaderBoardsReducer,
    withListContext,
    withFetch,
    withNotificationFieldsWhitelist,
    branch(
        ({ presentUpcomingRaces, presentOnAirRaces, presentEndedRaces, presentPendingRaces }) =>
            presentUpcomingRaces ||
            presentOnAirRaces ||
            presentPendingRaces ||
            presentEndedRaces,
        withSummaryUpdates
    )
);
